import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setUrl, setUserDetails } from "../Redux/action";
import tw from "tailwind-styled-components";
import Onboarding from "../Components/Checkout/Onboarding";
import Footer from "../Components/Checkout/Footer";
import Header from "../Components/Checkout/Header";
import { useQuery, useMutation } from "react-query";
import axios from "axios";
import Config from "../Config";
import NotFoundModel from "../Components/NotFoundModel";
import SkeletonLoader from "../Components/SkeletonLoader";
import { MdQrCodeScanner } from "react-icons/md";
import { FaRegCreditCard } from "react-icons/fa";
import { BsBank2 } from "react-icons/bs";
import { Sheet } from "react-modal-sheet";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { Images } from "../Images";
import moment from "moment/moment";
import { load } from "@cashfreepayments/cashfree-js";

const BgColor = tw.div`flex justify-center items-center bg-zinc-50`;
const PageWrapper = tw.div`flex flex-col w-full mx-auto md:max-w-sm bg-zinc-50 min-h-screen`;
const FooterWrapper = tw.div`w-full bg-zinc-50 py-3`;
const Title = tw.h2`text-lg font-bold`;
const SubTitle = tw.h2`text-sm text-gray-600`;
const Heading = tw.div`text-base font-semibold`;
const PayOnline = tw.button`bg-black flex justify-center gap-2 px-4 text-white h-11 items-center rounded-lg text-sm font-semibold w-full`;
const InputLabel = tw.div`block text-sm font-medium mb-2`;
const SubHeading = tw.p`text-sm md:text-[15px] font-normal`;
const Image = tw.img`w-24 h-24 md:w-44 md:h-44 mx-auto`;
const Box = tw.div`text-green-700 text-center p-4`;
const DuesAmount = tw.p`text-xs text-gray-600`;
const Amount = tw.p`text-base font-bold mb-2`;
const PaymentOption = tw.div`flex items-center border border-gray-300 p-4 rounded-lg space-x-6`;
const OptionLabel = tw.h4`text-base font-semibold`;
const OptionSubLabel = tw.p`text-sm`;
const SheetHeader = tw.div`border-b border-gray-200 p-4`;
const SheetTitle = tw.h3`text-lg font-semibold text-center`;
const CheckboxInput = tw.input`w-4 h-4 accent-[#07A5E2] bg-gray-100 border-gray-300 rounded focus:ring-blue-500`;
const PendingCard = tw.div`bg-zinc-50 rounded-t-3xl px-6 pt-6 mt-[-20px] relative z-10`;
const PayButton = tw.button`bg-black text-white rounded-full h-11 flex items-center justify-center mx-6 font-semibold text-sm`;
const DueItem = tw.div`bg-white rounded-xl p-3 mb-3 border border-gray-200 shadow-sm`;
const DueItemHeader = tw.div`flex justify-between items-start mb-2`;
const DueItemTitle = tw.h4`text-base font-semibold text-gray-900`;
const DueItemSubtitle = tw.p`text-xs text-gray-500`;
const DueItemAmount = tw.p`text-base font-semibold text-gray-900`;

const MakePayment = () => {
  const dispatch = useDispatch();
  const [step, setStep] = useState(1);
  const [userEmail, setUserEmail] = useState("");
  const [charges, setCharges] = useState("0");
  const [totalPayment, setTotalPayment] = useState("");
  const [selectedPaymentOption, setSelectedPaymentOption] = useState("");
  const [availableMethods, setAvailableMethods] = useState([]);
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [selectedDues, setSelectedDues] = useState([]);
  const [selectedDuesTotal, setSelectedDuesTotal] = useState(0);
  const [allSelected, setAllSelected] = useState(true);
  const [totalDuesAmount, setTotalDuesAmount] = useState(0);
  const [gstAmount, setGstAmount] = useState(0);
  const [hasSelectedDue, setHasSelectedDue] = useState(true);
  const shouldFetchChargesRef = useRef(false);
  const clickedPaymentMethodRef = useRef("");
  const [paymentEnabled, setPaymentEnabled] = useState(false);
  const [isCashfreeInitializing, setIsCashfreeInitializing] = useState(false);
  const [emailRequired, setEmailRequired] = useState(true);
  const [gateway, setGateway] = useState(1);

  const duesLabelMapping = {
    [Config.DUES_TYPES.RENT]: "Rent",
    [Config.DUES_TYPES.SECURITY]: "Security",
    [Config.DUES_TYPES.MAINTENANCE]: "Maintenance",
    [Config.DUES_TYPES.ELECTRICITY]: "Electricity",
    [Config.DUES_TYPES.REGISTRATION]: "Registration",
    [Config.DUES_TYPES.TECH_CHARGES]: "Technical Charges",
    [Config.DUES_TYPES.OTHER]: "Other",
    [Config.DUES_TYPES.AGREEMENT_CHARGE]: "Agreement Charges",
    [Config.DUES_TYPES.FOOD]: "Food",
    [Config.DUES_TYPES.FINE]: "Fine",
    [Config.DUES_TYPES.SETTLEMENT]: "Settlement",
    [Config.DUES_TYPES.WIFI]: "Wifi",
    [Config.DUES_TYPES.GAS]: "Gas",
    [Config.DUES_TYPES.MAID_CHARGES]: "Maid Charges",
    [Config.DUES_TYPES.DAMAGE]: "Damage",
    [Config.DUES_TYPES.KEY_CHARGES]: "Key Charges",
    [Config.DUES_TYPES.LAUNDRY_CHARGES]: "Laundry Charges",
    [Config.DUES_TYPES.MOVE_OUT_CHARGES]: "MoveOut Charges",
    [Config.DUES_TYPES.GUEST_CHARGES]: "Guest Charges",
  };

  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  const validationSchema = Yup.object().shape({
    email: Yup.string()
      .matches(emailRegex, "Invalid email format")
      .required("Email is required"),
  });

  useEffect(() => {
    dispatch(setUrl(window.location.href));
  }, [dispatch]);

  const gId = useSelector((state) => state.UserReducer.gId);
  const dueId = useSelector((state) => state.UserReducer.dueId);
  const token = useSelector(
    (state) => state.UserReducer.userDetails?.data?.data?.token
  );

  const fetchTenantDetails = async () => {
    if (!gId) return null;
    return await axios.get(
      `${Config.ApiUrl}/tenant/web/payment/${gId}/${dueId || undefined}`
    );
  };

  const {
    data,
    error,
    refetch,
    isLoading: isInitialLoading,
  } = useQuery("getTenantDetails", fetchTenantDetails, {
    enabled: !!gId,
    onSuccess: (data) => {
      dispatch(setUserDetails(data));
      processPaymentMethods(data);
      initializeSelectedDues(data);
      setPaymentEnabled(data?.data?.data?.paymentEnabled);
      const gatewayValue = data?.data?.data?.gateway || 1;
      setGateway(gatewayValue);
      setEmailRequired(data?.data?.data?.emailRequired)
    },
  });

  const isBookingPayment = data?.data?.data?.isBookingPayment;

  const initializeSelectedDues = (data) => {
    if (isBookingPayment) return;

    const dues = data?.data?.data?.dues || [];
    const initialSelectedDues = dues.map(due => ({
      id: due.id,
      ledgerReferenceId: due.ledgerReferenceId,
      balance: due.balance,
      type: due.type,
      title: due.title || duesLabelMapping[due.type],
      rentStartDate: due.rentStartDate,
      rentEndDate: due.rentEndDate,
      selected: true,
      dueDate: due.dueDate
    }));

    setSelectedDues(initialSelectedDues);
    const total = initialSelectedDues.reduce((sum, due) => sum + parseFloat(due.balance), 0);
    setSelectedDuesTotal(total);
    setHasSelectedDue(true);
  };

  const processPaymentMethods = (data) => {
    const methods = data?.data?.data?.paymentMethods || [];

    const { filtered, defaultMethod } = methods.reduce(
      (result, method) => {
        if (method.upi === 1) {
          result.filtered.push({
            type: Config.TRANSACTION_MODES.UPI,
            charges: method.charges,
          });
          if (!result.defaultMethod)
            result.defaultMethod = Config.TRANSACTION_MODES.UPI;
        }

        if (method.card === 1) {
          result.filtered.push({
            type: Config.TRANSACTION_MODES.CARD,
            charges: method.charges,
          });
          if (
            !result.defaultMethod &&
            result.defaultMethod !== Config.TRANSACTION_MODES.UPI
          )
            result.defaultMethod = Config.TRANSACTION_MODES.CARD;
        }

        if (method.netbanking === 1) {
          result.filtered.push({
            type: Config.TRANSACTION_MODES.NET_BANKING,
            charges: method.charges,
          });
          if (
            !result.defaultMethod &&
            result.defaultMethod !== Config.TRANSACTION_MODES.UPI &&
            result.defaultMethod !== Config.TRANSACTION_MODES.CARD
          )
            result.defaultMethod = Config.TRANSACTION_MODES.NET_BANKING;
        }

        return result;
      },
      { filtered: [], defaultMethod: "" }
    );

    setAvailableMethods(filtered);
    setSelectedPaymentOption(defaultMethod);
    clickedPaymentMethodRef.current = defaultMethod;
    const totalDues = data?.data?.data?.totalDues || 0;
    const gstAmountValue = data?.data?.data?.gstAmount || 0;
    // const defaultCharges =
    //   filtered.find((m) => m.type === defaultMethod)?.charges || "0";
    const defaultMethodObj = filtered.find(m => m.type === defaultMethod);
    const defaultCharges = defaultMethodObj?.initialCharges || "0";
    setCharges(defaultCharges);
    setTotalDuesAmount(totalDues);
    setGstAmount(gstAmountValue);

    const selectedTotal = isBookingPayment ? totalDues : totalDues;
    const total = (
      parseFloat(selectedTotal) +
      parseFloat(defaultCharges || 0) +
      parseFloat(gstAmountValue)
    ).toFixed(2);

    setTotalPayment(total);
    shouldFetchChargesRef.current = true;
  };

  useEffect(() => {
    if (gId) refetch();
  }, [gId, refetch]);

  useEffect(() => {
    if (step === 3 && selectedPaymentOption) {
      if (shouldFetchChargesRef.current) {
        fetchChargesForSelectedMethod(selectedPaymentOption);
        shouldFetchChargesRef.current = false;
      } else if (clickedPaymentMethodRef.current !== selectedPaymentOption) {
        fetchChargesForSelectedMethod(selectedPaymentOption);
        clickedPaymentMethodRef.current = selectedPaymentOption;
      }
    }
  }, [step, selectedPaymentOption]);


  const fetchChargesForSelectedMethod = (paymentMode = selectedPaymentOption) => {
    let amountToPay;
    if (isBookingPayment) {
      amountToPay = totalDuesAmount;
    } else {
      amountToPay = selectedDuesTotal;
    }

    if (amountToPay <= 0) return;

    const payload = {
      mode: paymentMode,
      amount: amountToPay,
    };

    chargesTransactionMutate(payload, {
      onSuccess: (response) => {
        const newCharges = response?.data?.data?.charges || "0";
        setCharges(newCharges);

        const total = (
          parseFloat(amountToPay) +
          parseFloat(newCharges || 0) +
          parseFloat(gstAmount)
        ).toFixed(2);

        setTotalPayment(total);
      },
      onError: (error) => {
        toast.error(error?.response?.data?.msg || "Failed to fetch charges");
        const selected = availableMethods.find((m) => m.type === paymentMode);
        const fallbackCharges = selected?.initialCharges || "0";
        setCharges(fallbackCharges);

        const total = (
          parseFloat(amountToPay) +
          parseFloat(fallbackCharges || 0) +
          parseFloat(gstAmount)
        ).toFixed(2);
        setTotalPayment(total);
      },
    });
  };


  const handleDueSelection = (dueId) => {
    const updatedDues = selectedDues.map(due =>
      due.id === dueId ? { ...due, selected: !due.selected } : due
    );

    const hasSelected = updatedDues.some(due => due.selected);

    setSelectedDues(updatedDues);
    setHasSelectedDue(hasSelected);

    // const selectedTotal = updatedDues
    //   .filter(due => due.selected)
    //   .reduce((sum, due) => sum + parseFloat(due.balance), 0);
    const selectedTotal = isBookingPayment
      ? totalDuesAmount
      : updatedDues
        .filter(due => due.selected)
        .reduce((sum, due) => sum + parseFloat(due.balance), 0);

    setSelectedDuesTotal(selectedTotal);

    setAllSelected(updatedDues.every(due => due.selected));

    // const total = (
    //   parseFloat(selectedTotal) +
    //   parseFloat(charges || 0) +
    //   parseFloat(gstAmount)
    // ).toFixed(2);

    // setTotalPayment(total);
    if (step === 3 && selectedPaymentOption) {
      fetchChargesForSelectedMethod();
    } else {
      const selected = availableMethods.find((m) => m.type === selectedPaymentOption);
      const currentCharges = selected?.initialCharges || "0";

      const total = (
        parseFloat(selectedTotal) +
        parseFloat(currentCharges || 0) +
        parseFloat(gstAmount)
      ).toFixed(2);
      setTotalPayment(total);
    }
  };

  // Handle payment option selection
  const handlePaymentOptionClick = (type) => {
    if (type === selectedPaymentOption) return;
    setSelectedPaymentOption(type);
  };

  // const handlePaymentOptionClick = (type) => {
  //   const selected = availableMethods.find((m) => m.type === type);
  //   setSelectedPaymentOption(type);
  //   const newCharges = selected?.charges || "0";
  //   setCharges(newCharges);

  //   const total = (
  //     // parseFloat(selectedDuesTotal) +
  //     parseFloat(isBookingPayment ? totalDuesAmount : selectedDuesTotal) +
  //     parseFloat(newCharges || 0) +
  //     parseFloat(gstAmount)
  //   ).toFixed(2);

  //   setTotalPayment(total);
  // };
  // Process transaction for Easebuzz
  const processEasebuzzTransaction = async (values) => {
    return await axios.post(`${Config.ApiUrl}/easebuzz/web-payment`, values, {
      headers: {
        authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });
  };
  const processPayUTransaction = async (values) => {
    return await axios.post(`${Config.ApiUrl}/hash/web/payment/new`, values, {
      headers: {
        authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });
  };
  const handlePayUTransactionSuccess = (data) => {
    document.body.insertAdjacentHTML("beforeend", data?.data);

    const payuForm = document.getElementById("payuForm");
    if (payuForm) {
      payuForm.submit();
    } else {
      console.error("Form not found in response");
    }
  };
  const handleEasebuzzTransactionSuccess = (response) => {
    if (response?.data && response?.data?.data?.payment_url) {
      window.location.href = response?.data?.data?.payment_url;
    } else {
      toast.success("Payment initiated successfully!");
      handleSheetClose();
    }
  };

  const handleTransactionError = (error) => {
    toast.error(error?.response?.data?.msg || "Payment processing error");
  };
  const {
    isLoading: isPayUTransactionLoading,
    mutate: payUTransactionMutate
  } = useMutation(processPayUTransaction, {
    onSuccess: handlePayUTransactionSuccess,
    onError: handleTransactionError,
  });
  const {
    isLoading: isTransactionLoading,
    mutate: easebuzzTransactionMutate
  } = useMutation(processEasebuzzTransaction, {
    onSuccess: handleEasebuzzTransactionSuccess,
    onError: handleTransactionError,
  });


  const processCashFreeOrderStatusTransaction = async (values) => {
    return await axios.post(`${Config.ApiUrl}/cashfree/orderstatus`, values, {
      headers: {
        authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });
  };
  const handleOrderTransactionSuccess = (data) => {
    if (data?.data?.data?.order_status === "PAID") {
      window.location.href = "/payment-success";
    } else {
      window.location.href = "/payment-failed";
    }
  };
  const handleOrderStatusTransactionError = (error) => {
    window.location.href = `/payment-failed`;
  };
  const { isLoading: isOrderStatusTransactionLoading, mutate: orderStatusTransactionMutate } =
    useMutation(processCashFreeOrderStatusTransaction, {
      onSuccess: handleOrderTransactionSuccess,
      onError: handleOrderStatusTransactionError,
    });

  const tenantData =
    data?.data?.data;
  const propGID = tenantData?.propGId;
  const tenantId = tenantData?.tenantId;
  const customerId =
    propGID && tenantId ? `${propGID}_${tenantId}` : "";
  // New function to handle Cashfree payment using SDK
  const handleCashfreePayment = async (payload) => {
    setIsCashfreeInitializing(true);
    let selectedDueItems;
    let amountToPay;

    // if (enableDueSelection) {
    selectedDueItems = selectedDues.filter((due) => due.selected);

    if (selectedDueItems.length === 0) {
      toast.error("Please select at least one due");
      return;
    }

    amountToPay = isBookingPayment ? totalDuesAmount : selectedDuesTotal;
    // } 
    // else {
    //   selectedDueItems = selectedDues;
    //   amountToPay = totalDuesAmount;
    // }
    try {

      const res = await axios.post(
        `${Config.ApiUrl}/cashfree/createorder`,
        {
          email: userEmail,
          mode: selectedPaymentOption,
          // dueId: selectedDueItems.map((due) => due.id),
          dueId: selectedDueItems.map((due) => due.id).join(","),
          ledgerReferenceId: selectedDueItems.map((due) => due.ledgerReferenceId),
          amount: amountToPay,
          charges: charges,
          gstAmount: gstAmount,
          payingFor: isBookingPayment ? 3 : 2,
          customerDetails: {
            customer_id: customerId || "",
            customer_name: tenantData?.tenantName || "",
            customer_phone: tenantData?.tenantMobile?.toString() || "",
          },
          // isBookingPayment,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      const paymentSessionId = res?.data?.data?.payment_session_id;
      const orderId = res?.data?.data?.order_id
      if (!paymentSessionId) {
        toast.error("Payment session not received");
        return;
      }

      // const cashfree = await load({
      //   mode: "sandbox",
      // });
      const cashfree = await load({ mode: "production" });

      // await cashfree.checkout({
      //   paymentSessionId: paymentSessionId,
      //   redirectTarget: "_self",
      // });
      await cashfree.checkout({
        paymentSessionId,
        redirectTarget: "_modal",
      }).then((result) => {
        if (result.error) {
          orderStatusTransactionMutate({ orderId })
          toast.error(result.error.message);
        } else {
          toast.success("Payment completed");
          orderStatusTransactionMutate({ orderId })
        }
      });

    } catch (error) {
      toast.error(
        error?.response?.data?.msg || "Cashfree payment failed"
      );

    } finally {
      setIsCashfreeInitializing(false);
    }
  };

  // const getPaymentGateway = (paymentMethod) => {
  //   return paymentMethod === Config.TRANSACTION_MODES.UPI || paymentMethod === Config.TRANSACTION_MODES.NET_BANKING
  //     ? Config.PAYMENT_GATEWAY.EASEBUZZ
  //     : Config.PAYMENT_GATEWAY.PAYU;
  // };

  const getPaymentGateway = (paymentMethod) => {
    const gatewayFromApi = gateway;
    if (
      gatewayFromApi === Config.PAYMENT_GATEWAY.EASEBUZZ &&
      (paymentMethod === Config.TRANSACTION_MODES.UPI || paymentMethod === Config.TRANSACTION_MODES.NET_BANKING)
    ) {
      return Config.PAYMENT_GATEWAY.EASEBUZZ;
    } else if (gatewayFromApi === Config.PAYMENT_GATEWAY.CASHFREE) 
	{
      return Config.PAYMENT_GATEWAY.CASHFREE;
    }
    return Config.PAYMENT_GATEWAY.PAYU;
  };

  // const getIsLoading = () => {
  //   const gateway = getPaymentGateway(selectedPaymentOption);

  //   if (gateway === Config.PAYMENT_GATEWAY.EASEBUZZ) {
  //     return isTransactionLoading;
  //   }
  //   return isPayUTransactionLoading;
  // };
  const getIsLoading = () => {
    const selectedGateway = getPaymentGateway(selectedPaymentOption);
    if (selectedGateway === Config.PAYMENT_GATEWAY.EASEBUZZ) {
      return isTransactionLoading;
    } else if (selectedGateway === Config.PAYMENT_GATEWAY.CASHFREE) {
      // return isCashFreeTransactionLoading;
      return isCashfreeInitializing;
    }
    return isPayUTransactionLoading;
  };
  const handlePayNow = () => {
    const selectedDueItems = selectedDues.filter(due => due.selected);

    if (selectedDueItems.length === 0) {
      toast.error("Please select at least one due");
      return;
    }

    const payload = {
      email: userEmail,
      mode: selectedPaymentOption,
      dueId: selectedDueItems.map((due) => due.id),
      ledgerReferenceId: selectedDueItems.map((due) => due.ledgerReferenceId),
      amount: isBookingPayment ? totalDuesAmount : selectedDuesTotal,
      charges: charges,
      gstAmount: gstAmount,
      payingFor: isBookingPayment ? 3 : 2,
      // isBookingPayment,
    };
    // const gateway = getPaymentGateway(selectedPaymentOption);

    // if (gateway === Config.PAYMENT_GATEWAY.EASEBUZZ) {
    //   easebuzzTransactionMutate(payload);
    // } else {
    //   payUTransactionMutate(payload);
    // }
    const selectedGateway = getPaymentGateway(selectedPaymentOption);
    if (selectedGateway === Config.PAYMENT_GATEWAY.EASEBUZZ) {
      easebuzzTransactionMutate(payload);
    } else if (selectedGateway === Config.PAYMENT_GATEWAY.CASHFREE) {
      // cashFreeTransactionMutate(payload);
      handleCashfreePayment(payload);
    } else {
      payUTransactionMutate(payload);
    }
  };

  // const handleProceedToStep2 = () => {
  //   handleNextToStep2();
  // };
  const handleNextToStep2 = () => {
    if (emailRequired === false) {
      setStep(3);
      setIsSheetOpen(true);
    } else {
      setStep(2);
      setIsSheetOpen(true);
    }
  };
  const renderPaymentMethodLabel = (type) => {
    switch (type) {
      case Config.TRANSACTION_MODES.UPI:
        return "UPI";
      case Config.TRANSACTION_MODES.CARD:
        return "Card";
      case Config.TRANSACTION_MODES.NET_BANKING:
        return "Net Banking";
      default:
        return "Offline";
    }
  };

  const renderPaymentMethodDescription = (type) => {
    switch (type) {
      case Config.TRANSACTION_MODES.UPI:
        return "Pay instantly using your UPI ID or app";
      case Config.TRANSACTION_MODES.CARD:
        return "Use your credit or debit card";
      case Config.TRANSACTION_MODES.NET_BANKING:
        return "Pay securely using net banking";
      default:
        return "";
    }
  };

  const renderPaymentMethodIcon = (type) => {
    switch (type) {
      case Config.TRANSACTION_MODES.UPI:
        return <MdQrCodeScanner className="w-7 h-7" />;
      case Config.TRANSACTION_MODES.CARD:
        return <FaRegCreditCard className="w-7 h-7" />;
      case Config.TRANSACTION_MODES.NET_BANKING:
        return <BsBank2 className="w-7 h-7" />;
      default:
        return null;
    }
  };

  // const handleNextToStep2 = () => {
  //   setStep(2);
  //   setIsSheetOpen(true);
  // };

  const handleSheetClose = () => {
    setIsSheetOpen(false);
    setStep(1);
    shouldFetchChargesRef.current = true;
  };

  const getCurrentTarget = (e) => {
    const targetClass = Array.from(e.target.classList);
    if (targetClass.includes("addCommentModel")) handleSheetClose();
  };

  const getSheetTitle = () => {
    if (step === 2) return "Enter Your Email";
    if (step === 3) return "Select Payment Method";
    return "";
  };

  const formatDateRange = (due) => {
    if (due.type === Config.DUES_TYPES.SECURITY) {
      return "Initial security deposit";
    } else if (due.rentStartDate && due.rentEndDate) {
      return `${moment(due.rentStartDate).format("DD MMM YY")} - ${moment(due.rentEndDate).format("DD MMM YY")}`;
    } else if (due.dueDate) {
      return `For ${moment(due.dueDate).format("MMM YY")}`;
    }
    return "";
  };

  const chargesTransaction = async (values) => {
    return await axios.post(`${Config.ApiUrl}/transaction/charges/get`, values, {
      headers: {
        authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });
  };

  const chargesTransactionError = (error) => {
  };

  const { isLoading: chargesTransactionLoading, mutate: chargesTransactionMutate } =
    useMutation(chargesTransaction, {
      onError: chargesTransactionError,
    });


  if (error) return <NotFoundModel />;
  const isLoading = getIsLoading();
  return (
    <>
      <BgColor>
        <PageWrapper>
          {isInitialLoading ? (
            <SkeletonLoader />
          ) : (
            <>
              <Header HeaderData={data?.data?.data} />
              <div className="flex-grow">
                <div className="relative">
                  <Onboarding RoomData={data?.data?.data?.occupancy} />
                </div>

                {step === 1 && (
                  <div className="relative">
                    {totalDuesAmount !== 0 ? (
                      <PendingCard>
                        <div className="mb-4">
                          <Title>Pending Dues</Title>
                          <SubTitle>Please complete your payment</SubTitle>
                        </div>
                        {selectedDues.length > 0 && (
                          <div className="mb-4">
                            {selectedDues.map((due, index) => (
                              <React.Fragment key={due.id}>
                                <DueItem>
                                  <DueItemHeader>
                                    <div className="flex-1">
                                      <DueItemTitle>
                                        {isBookingPayment ? "Booking" : (due.title || duesLabelMapping[due.type])}

                                      </DueItemTitle>
                                      <DueItemSubtitle>
                                        {isBookingPayment ? 'Initial booking amount' : (formatDateRange(due))}
                                      </DueItemSubtitle>
                                    </div>
                                    <div className="flex items-center gap-3">
                                      <DueItemAmount>₹{isBookingPayment ? (totalDuesAmount) : (due.balance)}</DueItemAmount>
                                      <CheckboxInput
                                        type="checkbox"
                                        checked={due.selected}
                                        onChange={() => handleDueSelection(due.id)}
                                        className="cursor-pointer"
                                      />
                                    </div>
                                  </DueItemHeader>
                                </DueItem>
                              </React.Fragment>
                            ))}
                          </div>
                        )}



                      </PendingCard>
                    ) : (
                      <Box>
                        <Image
                          src={Images.SuccessGif || "/placeholder.svg"}
                          alt="Payment Success"
                        />
                        <Heading>Payment Done!</Heading>
                        <SubHeading>
                          Your payment has been successfully processed! There
                          are no pending dues at the moment. Thank you!
                        </SubHeading>
                      </Box>
                    )}
                  </div>
                )}
              </div>
              {totalDuesAmount !== 0 && (
                <PayButton
                  onClick={handleNextToStep2}
                  disabled={!hasSelectedDue || !paymentEnabled}
                  className={(!hasSelectedDue || !paymentEnabled) ? "opacity-60 cursor-not-allowed" : ""}
                >
                  Pay ( ₹{isBookingPayment ? totalDuesAmount : selectedDuesTotal} )
                </PayButton>)}
              <FooterWrapper>
                <Footer />
              </FooterWrapper>
            </>
          )}
        </PageWrapper>
      </BgColor>
      {isSheetOpen && (
        <div
          className="fixed inset-0 z-40 bg-neutral-900 bg-opacity-70 backdrop-blur-sm transition-opacity addCommentModel"
          onClick={getCurrentTarget}
        />
      )}

      <Sheet
        isOpen={isSheetOpen}
        onClose={handleSheetClose}
        className="mx-auto md:max-w-sm "
        detent="content-height"
        disableScrollLocking={true}
        overlayClassName="bg-neutral-900 backdrop-blur-sm bg-opacity-70"
      >
        <Sheet.Container>
          <SheetHeader>
            <SheetTitle>{getSheetTitle()}</SheetTitle>
          </SheetHeader>
          <Sheet.Content>
            <Sheet.Scroller className="overflow-y-auto">
              {step === 2 && (
                <div className="px-4 py-4">
                  <Formik
                    initialValues={{ email: "" }}
                    validationSchema={validationSchema}
                    onSubmit={(values) => {
                      setUserEmail(values.email);
                      setStep(3);
                    }}
                  >
                    {({ handleSubmit, errors, touched }) => (
                      <Form onSubmit={handleSubmit}>
                        <InputLabel>Email *</InputLabel>
                        <Field
                          type="email"
                          name="email"
                          placeholder="Enter your email"
                          className="w-full px-2 h-11 border rounded-lg"
                        />
                        {errors.email && touched.email && (
                          <div className="text-red-500 text-sm">
                            {errors.email}
                          </div>
                        )}

                        <div className="flex justify-center items-center mt-5 gap-4">
                          <PayOnline type="submit">Continue</PayOnline>
                        </div>
                      </Form>
                    )}
                  </Formik>
                </div>
              )}

              {step === 3 && (
                <div className="px-4 py-4 space-y-4">
                  <div className="flex justify-between">
                    <div>
                      <DuesAmount>
                        {isBookingPayment ? "Booking Amount" : "Selected Dues Amount"}
                      </DuesAmount>
                      <Amount>₹{isBookingPayment ? totalDuesAmount : selectedDuesTotal}</Amount>
                    </div>
                    {data?.data?.data?.isGSTInvoice ===
                      Config.GST_ENABLED.YES && (
                        <div>
                          <DuesAmount>GST Charges</DuesAmount>
                          <Amount>₹{gstAmount}</Amount>
                        </div>
                      )}
                    <div>
                      <DuesAmount>Convenience fees</DuesAmount>
                      {/* <Amount>₹{charges}</Amount> */}
                      <Amount>
                        {chargesTransactionLoading ? "Calculating..." : `₹${charges}`}
                      </Amount>
                    </div>
                  </div>

                  <h3>Please select a payment method to proceed.</h3>

                  {availableMethods.map((method) => (
                    <PaymentOption
                      key={method.type}
                      onClick={() => !chargesTransactionLoading && handlePaymentOptionClick(method.type)}
                      className={`${selectedPaymentOption === method.type
                        ? "bg-blue-500 text-white"
                        : "bg-white text-gray-600"
                        } cursor-pointer ${chargesTransactionLoading ? "opacity-50 cursor-not-allowed" : ""}`}
                    >
                      {renderPaymentMethodIcon(method.type)}
                      <div>
                        <OptionLabel>
                          {renderPaymentMethodLabel(method.type)}
                        </OptionLabel>
                        <OptionSubLabel
                          className={`${selectedPaymentOption === method.type
                            ? "text-white"
                            : "text-gray-400"
                            }`}
                        >
                          {renderPaymentMethodDescription(method.type)}
                        </OptionSubLabel>
                      </div>
                    </PaymentOption>
                  ))}

                  <div className="flex justify-between items-center border-t border-gray-300 pt-4 mt-4">
                    <div>
                      <DuesAmount>Total Amount</DuesAmount>
                      <Amount>₹{totalPayment}</Amount>
                    </div>
                    <div>
                      <PayOnline
                        disabled={isLoading || chargesTransactionLoading}
                        className={`${isLoading || chargesTransactionLoading
                          ? "opacity-50 cursor-not-allowed"
                          : ""
                          }`}
                        onClick={handlePayNow}
                      >
                        {isLoading ? "Processing..." : "Pay Now"}
                      </PayOnline>
                    </div>
                  </div>
                </div>
              )}
            </Sheet.Scroller>
          </Sheet.Content>
        </Sheet.Container>
      </Sheet>
    </>
  );
};

export default MakePayment;
